import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import {
  ContactFieldDefinition,
  downloadImportRejectedCsv,
  downloadImportPreviewRejectedCsv,
  getContactFieldDefinitions,
  getImportSession,
  getImportSessions,
  getQueueHealth,
  fetchAllTags,
  importContactsCsv,
  loadTagOptions,
  previewContactsImport,
  ImportPreviewResult,
  ImportPreviewAsyncAccepted,
  ImportSessionProgress,
  QueueHealth,
  Tag,
} from "../../helpers/audience_helper";
import {
  CsvPreview,
  downloadContactsCsvTemplate,
  guessColumnMap,
  parseCsvPreview,
} from "../../helpers/csvPreview";
import { helpUrlForRoute } from "../../constants/helpRouteMap";

/** Linhas de higiene: e-mail vs domínio (sessões antigas caem no total). */
function ImportSuppressionItems({
  progress,
}: {
  progress: ImportSessionProgress;
}) {
  const { t } = useTranslation();
  const emailCount = progress.email_suppressed_rows ?? 0;
  const domainCount = progress.domain_suppressed_rows ?? 0;
  const legacy = progress.suppressed_rows ?? 0;
  const showLegacy = emailCount === 0 && domainCount === 0 && legacy > 0;

  return (
    <>
      {emailCount > 0 && (
        <li>
          {t("contacts.import.result.emailSuppressed", { count: emailCount })}
        </li>
      )}
      {domainCount > 0 && (
        <li>
          {t("contacts.import.result.domainSuppressed", { count: domainCount })}
        </li>
      )}
      {showLegacy && (
        <li>
          {t("contacts.import.result.suppressed", { count: legacy })}
        </li>
      )}
    </>
  );
}

type TagOption = { value: number; label: string };

const POLL_MS = 2000;
const STUCK_HINT_MS = 12000;
const TERMINAL = new Set(["completed", "failed"]);

/**
 * Import CSV — upload, mapeamento (nativos + slugs), preview, polling (DOC 09 / 13 · LAC-079).
 */
const ImportContacts = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  document.title = t("contacts.import.documentTitle");
  const dirtyListHelpHref = helpUrlForRoute(
    "/contacts/import",
    i18n.language || "pt-BR"
  );

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [emailColumn, setEmailColumn] = useState("");
  const [nameColumn, setNameColumn] = useState("");
  const [phoneColumn, setPhoneColumn] = useState("");
  /** slug → header CSV escolhido. */
  const [fieldColumns, setFieldColumns] = useState<Record<string, string>>({});
  const [fieldDefs, setFieldDefs] = useState<ContactFieldDefinition[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [recent, setRecent] = useState<ImportSessionProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [progress, setProgress] = useState<ImportSessionProgress | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [showStuckHint, setShowStuckHint] = useState(false);
  const [queueHealth, setQueueHealth] = useState<QueueHealth | null>(null);
  const [dryRun, setDryRun] = useState<ImportPreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [downloadingPreviewRejects, setDownloadingPreviewRejects] =
    useState(false);
  const [downloadingRejected, setDownloadingRejected] = useState(false);
  const toastedRef = useRef(false);
  const resumeTriedRef = useRef(false);

  const tagOptions: TagOption[] = useMemo(
    () => tags.map((tag) => ({ value: tag.id, label: tag.name })),
    [tags]
  );

  const sortedFieldDefs = useMemo(
    () =>
      [...fieldDefs].sort(
        (a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)
      ),
    [fieldDefs]
  );

  const headerOptions = useMemo(
    () => (preview?.headers || []).map((h) => ({ value: h, label: h })),
    [preview]
  );

  const status = progress?.status;
  const isTerminal = status ? TERMINAL.has(status) : false;
  const isPolling = sessionId !== null && !isTerminal;

  const loadRecent = useCallback(async () => {
    try {
      const res = await getImportSessions(8);
      if (res?.success) {
        setRecent(res.data || []);
      }
    } catch {
      // Histórico é auxiliar — não bloqueia o fluxo.
    }
  }, []);

  useEffect(() => {
    fetchAllTags()
      .then((list) => setTags(list))
      .catch(() => undefined);
    getContactFieldDefinitions({ per_page: 30 })
      .then((res) => {
        if (res?.success) {
          setFieldDefs(res.data || []);
        }
      })
      .catch(() => undefined);
    void loadRecent();
  }, [loadRecent]);

  /** Se o header do CSV bate com slug ou label, pré-seleciona o mapeamento. */
  const guessFieldColumns = useCallback(
    (headers: string[], defs: ContactFieldDefinition[]) => {
      const guessed: Record<string, string> = {};
      for (const def of defs) {
        const hit = headers.find((h) => {
          const key = h.toLowerCase().trim();
          return (
            key === def.slug.toLowerCase() ||
            key === def.label.toLowerCase().trim()
          );
        });
        if (hit) {
          guessed[def.slug] = hit;
        }
      }
      return guessed;
    },
    []
  );
  useEffect(() => {
    if (!preview || sortedFieldDefs.length === 0) {
      return;
    }
    setFieldColumns((prev) => {
      if (Object.keys(prev).length > 0) {
        return prev;
      }
      return guessFieldColumns(preview.headers, sortedFieldDefs);
    });
  }, [preview, sortedFieldDefs, guessFieldColumns]);

  const attachSession = useCallback((id: number, replaceUrl = true) => {
    setSessionId(id);
    if (replaceUrl) {
      setSearchParams({ session: String(id) }, { replace: true });
    }
  }, [setSearchParams]);

  const clearSessionUrl = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Retoma progresso após F5 via ?session=
  useEffect(() => {
    if (resumeTriedRef.current) {
      return;
    }
    const raw = searchParams.get("session");
    const id = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    resumeTriedRef.current = true;
    attachSession(id, false);
  }, [searchParams, attachSession]);

  const pollOnce = useCallback(async (id: number) => {
    try {
      const res = await getImportSession(id);
      if (!res?.success || !res.data) {
        setError(t("contacts.import.error.progress"));
        return;
      }
      setProgress(res.data);
    } catch (err: any) {
      setError(err?.message || t("contacts.import.error.progress"));
    }
  }, [t]);

  useEffect(() => {
    if (!sessionId || isTerminal) {
      return;
    }

    void pollOnce(sessionId);
    const timer = window.setInterval(() => {
      void pollOnce(sessionId);
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [sessionId, isTerminal, pollOnce]);

  // Aviso se fica “na fila” sem avançar — LAC-028 consulta saúde real do worker.
  useEffect(() => {
    if (!isPolling || status !== "pending") {
      setShowStuckHint(false);
      setQueueHealth(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowStuckHint(true);
      void getQueueHealth()
        .then((res) => {
          if (res?.success && res.data) {
            setQueueHealth(res.data);
          }
        })
        .catch(() => setQueueHealth(null));
    }, STUCK_HINT_MS);
    return () => window.clearTimeout(timer);
  }, [isPolling, status, sessionId]);

  useEffect(() => {
    if (!progress || toastedRef.current) {
      return;
    }
    if (progress.status === "completed") {
      toastedRef.current = true;
      toast.success(apiMessage || t("contacts.import.toast.completed"), {
        autoClose: 3500,
      });
      void loadRecent();
    }
    if (progress.status === "failed") {
      toastedRef.current = true;
      toast.error(
        progress.failure_message || t("contacts.import.toast.failed"),
        { autoClose: 4500 }
      );
      void loadRecent();
    }
  }, [progress, apiMessage, t, loadRecent]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0] || null;
    setFile(next);
    setError(null);
    setPreview(null);
    setEmailColumn("");
    setNameColumn("");
    setPhoneColumn("");
    setFieldColumns({});

    if (!next) {
      return;
    }

    try {
      const text = await next.slice(0, 64 * 1024).text();
      const parsed = parseCsvPreview(text, 5);
      if (!parsed) {
        setError(t("contacts.import.error.preview"));
        return;
      }
      setPreview(parsed);
      const guess = guessColumnMap(parsed.headers);
      setEmailColumn(guess.email);
      setNameColumn(guess.name);
      setPhoneColumn(guess.phone);
      setFieldColumns(guessFieldColumns(parsed.headers, sortedFieldDefs));
    } catch {
      setError(t("contacts.import.error.preview"));
    }
  };

  const resetForRetry = () => {
    setFile(null);
    setPreview(null);
    setEmailColumn("");
    setNameColumn("");
    setPhoneColumn("");
    setFieldColumns({});
    setSessionId(null);
    setProgress(null);
    setApiMessage(null);
    setError(null);
    setShowStuckHint(false);
    setQueueHealth(null);
    setDryRun(null);
    toastedRef.current = false;
    resumeTriedRef.current = true;
    clearSessionUrl();
  };

  const buildColumnMap = (): Record<string, string> => {
    const columnMap: Record<string, string> = {
      email: emailColumn,
    };
    if (nameColumn) {
      columnMap.name = nameColumn;
    }
    if (phoneColumn) {
      columnMap.phone = phoneColumn;
    }
    Object.entries(fieldColumns).forEach(([slug, header]) => {
      if (header) {
        columnMap[slug] = header;
      }
    });
    return columnMap;
  };

  const runDryRun = async () => {
    if (!file || !emailColumn) {
      setError(
        !file
          ? t("contacts.import.error.fileRequired")
          : t("contacts.import.error.emailColumn")
      );
      return;
    }
    setPreviewing(true);
    setPreviewProgress(8);
    setError(null);
    setDryRun(null);
    // Progresso visual — o server não streama %; anima até ~90% enquanto espera.
    const tick = window.setInterval(() => {
      setPreviewProgress((p) =>
        p >= 90 ? p : p + Math.max(1, Math.round((90 - p) / 8))
      );
    }, 400);
    try {
      const res = await previewContactsImport(file, {
        columnMap: buildColumnMap(),
      });
      if (!res?.success || !res.data) {
        setError(t("contacts.import.preview.error"));
        return;
      }

      const data = res.data as ImportPreviewResult &
        Partial<ImportPreviewAsyncAccepted>;

      // Arquivo grande → 202 + Job; poll GET /imports/{id} até preview pronto.
      if (data.async === true && data.id) {
        toast.info(res.message || t("contacts.import.preview.asyncToast"), {
          autoClose: 3500,
        });
        let attempts = 0;
        while (attempts < 90) {
          attempts += 1;
          await new Promise((r) => window.setTimeout(r, 2000));
          const poll = await getImportSession(data.id);
          if (!poll?.success || !poll.data) {
            continue;
          }
          setPreviewProgress(
            Math.min(95, 10 + (poll.data.progress_percent || attempts))
          );
          if (poll.data.status === "failed") {
            setError(
              poll.data.failure_message || t("contacts.import.preview.error")
            );
            return;
          }
          if (poll.data.status === "completed" && poll.data.preview) {
            setDryRun(poll.data.preview);
            setPreviewProgress(100);
            toast.info(t("contacts.import.preview.toast"), {
              autoClose: 2500,
            });
            return;
          }
        }
        setError(t("contacts.import.preview.asyncTimeout"));
        return;
      }

      setPreviewProgress(100);
      setDryRun(data as ImportPreviewResult);
      toast.info(t("contacts.import.preview.toast"), { autoClose: 2500 });
    } catch (err: any) {
      setError(err?.message || t("contacts.import.preview.error"));
    } finally {
      window.clearInterval(tick);
      setPreviewing(false);
      window.setTimeout(() => setPreviewProgress(0), 600);
    }
  };

  const downloadFullPreviewRejects = async () => {
    if (!file || !emailColumn) {
      return;
    }
    setDownloadingPreviewRejects(true);
    try {
      await downloadImportPreviewRejectedCsv(file, {
        columnMap: buildColumnMap(),
      });
      toast.info(t("contacts.import.preview.downloadFullToast"), {
        autoClose: 2500,
      });
    } catch (err: any) {
      toast.error(
        err?.message || t("contacts.import.preview.downloadFullError"),
        { autoClose: 4000 }
      );
    } finally {
      setDownloadingPreviewRejects(false);
    }
  };

  const startImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError(t("contacts.import.error.fileRequired"));
      return;
    }
    if (!emailColumn) {
      setError(t("contacts.import.error.emailColumn"));
      return;
    }

    setUploading(true);
    setError(null);
    toastedRef.current = false;

    try {
      const columnMap = buildColumnMap();

      const res = await importContactsCsv(file, {
        tagIds: selectedTags.map((opt) => opt.value),
        columnMap,
      });
      if (!res?.success || !res.data?.id) {
        setError(t("contacts.import.error.upload"));
        return;
      }
      setApiMessage(res.message || null);
      attachSession(res.data.id);
      setProgress({
        id: res.data.id,
        status: res.data.status || "pending",
        total_rows: 0,
        processed_rows: 0,
        imported_rows: 0,
        duplicated_rows: 0,
        failed_rows: 0,
        progress_percent: 0,
        failure_message: null,
      });
      toast.info(res.message || t("contacts.import.toast.queued"), {
        autoClose: 2500,
      });
      void loadRecent();
    } catch (err: any) {
      const details = err?.details?.file || err?.details?.["column_map.email"];
      setError(
        Array.isArray(details)
          ? details[0]
          : err?.message || t("contacts.import.error.upload")
      );
    } finally {
      setUploading(false);
    }
  };

  const resumeRecent = (id: number) => {
    toastedRef.current = false;
    setError(null);
    setShowStuckHint(false);
    attachSession(id);
    setProgress(null);
  };

  const percent = progress?.progress_percent ?? 0;
  const showForm = sessionId === null;

  const formatWhen = (iso?: string) => {
    if (!iso) {
      return "—";
    }
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("contacts.import.title")}
          actions={
            <PageHelpLink
              href={dirtyListHelpHref}
              label={t("contacts.import.help")}
            />
          }
        />

        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <h5 className="card-title mb-0">{t("contacts.import.title")}</h5>
                {showForm && (
                  <Button
                    color="link"
                    className="p-0"
                    type="button"
                    onClick={() =>
                      downloadContactsCsvTemplate(
                        t("contacts.import.templateFilename")
                      )
                    }
                  >
                    <i className="ri-download-2-line me-1" />
                    {t("contacts.import.downloadTemplate")}
                  </Button>
                )}
              </CardHeader>
              <CardBody>
                {showForm ? (
                  <Form onSubmit={startImport}>
                    <div className="text-center mb-4">
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-file-upload-line" />
                        </div>
                      </div>
                      <p className="text-muted mb-0">
                        {t("contacts.import.description")}
                      </p>
                    </div>

                    {/* ToS / lista limpa — persistente, não esconder no rodapé (DOC 04 § 4.5.1). */}
                    <Alert color="warning" className="mb-3">
                      <p className="mb-2 fw-medium">
                        {t("contacts.import.dirtyListWarning.title")}
                      </p>
                      <p className="mb-2 mb-md-0">
                        {t("contacts.import.dirtyListWarning.body")}{" "}
                        <a
                          href={dirtyListHelpHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fw-medium"
                        >
                          {t("contacts.import.dirtyListWarning.helpLink")}
                        </a>
                      </p>
                    </Alert>

                    <Alert color="info" className="mb-3">
                      {t("contacts.import.formatHint")}
                    </Alert>

                    <div className="mb-3">
                      <Label htmlFor="contacts-import-file">
                        {t("contacts.import.fileLabel")}
                      </Label>
                      <Input
                        id="contacts-import-file"
                        type="file"
                        accept=".csv,.txt,text/csv,text/plain"
                        onChange={(ev) => void onFileChange(ev)}
                        disabled={uploading}
                      />
                      {file && (
                        <small className="text-muted d-block mt-1">
                          {t("contacts.import.fileSelected", {
                            name: file.name,
                          })}
                        </small>
                      )}
                    </div>

                    {preview && (
                      <>
                        <div className="mb-3">
                          <Label htmlFor="contacts-import-email-col">
                            {t("contacts.import.map.email")}
                          </Label>
                          <Input
                            id="contacts-import-email-col"
                            type="select"
                            value={emailColumn}
                            onChange={(e) => setEmailColumn(e.target.value)}
                            disabled={uploading}
                            required
                          >
                            <option value="">
                              {t("contacts.import.map.placeholder")}
                            </option>
                            {headerOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Input>
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="contacts-import-name-col">
                            {t("contacts.import.map.name")}
                          </Label>
                          <Input
                            id="contacts-import-name-col"
                            type="select"
                            value={nameColumn}
                            onChange={(e) => setNameColumn(e.target.value)}
                            disabled={uploading}
                          >
                            <option value="">
                              {t("contacts.import.map.none")}
                            </option>
                            {headerOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Input>
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="contacts-import-phone-col">
                            {t("contacts.import.map.phone")}
                          </Label>
                          <Input
                            id="contacts-import-phone-col"
                            type="select"
                            value={phoneColumn}
                            onChange={(e) => setPhoneColumn(e.target.value)}
                            disabled={uploading}
                          >
                            <option value="">
                              {t("contacts.import.map.none")}
                            </option>
                            {headerOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Input>
                          <div className="form-text">
                            {t("contacts.import.map.phoneHelp")}
                          </div>
                        </div>

                        {sortedFieldDefs.length > 0 && (
                          <div className="mb-3">
                            <Label className="d-block mb-2">
                              {t("contacts.import.map.customSection")}
                            </Label>
                            <p className="text-muted small mb-2">
                              {t("contacts.import.map.customHint")}
                            </p>
                            {sortedFieldDefs.some((d) => d.type === "date") && (
                              <p className="text-muted small mb-2">
                                {t("contacts.import.map.dateFormatHint")}
                              </p>
                            )}
                            <div className="vstack gap-2">
                              {sortedFieldDefs.map((def) => (
                                <div key={def.slug}>
                                  <Label
                                    htmlFor={`contacts-import-field-${def.slug}`}
                                    className="small mb-1"
                                  >
                                    {def.label}
                                    <span className="text-muted">
                                      {" "}
                                      ({def.slug}
                                      {def.type === "date"
                                        ? ` · ${t("contacts.import.map.dateTypeSuffix")}`
                                        : ""}
                                      )
                                    </span>
                                  </Label>
                                  <Input
                                    id={`contacts-import-field-${def.slug}`}
                                    type="select"
                                    bsSize="sm"
                                    value={fieldColumns[def.slug] || ""}
                                    onChange={(e) =>
                                      setFieldColumns((prev) => {
                                        const next = { ...prev };
                                        if (!e.target.value) {
                                          delete next[def.slug];
                                        } else {
                                          next[def.slug] = e.target.value;
                                        }
                                        return next;
                                      })
                                    }
                                    disabled={uploading}
                                  >
                                    <option value="">
                                      {t("contacts.import.map.customNone")}
                                    </option>
                                    {headerOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </Input>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mb-4">
                          <Label className="d-block">
                            {t("contacts.import.preview.title")}
                          </Label>
                          <div className="table-responsive border rounded">
                            <Table size="sm" className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  {preview.headers.map((h) => (
                                    <th key={h}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {preview.rows.map((row, idx) => (
                                  <tr key={idx}>
                                    {preview.headers.map((_, colIdx) => (
                                      <td key={colIdx}>
                                        {row[colIdx] || "—"}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                          <small className="text-muted">
                            {t("contacts.import.preview.hint")}
                          </small>
                        </div>
                      </>
                    )}

                    <div className="mb-4">
                      <Label htmlFor="contacts-import-tags">
                        {t("contacts.import.tagsLabel")}
                      </Label>
                      <AsyncSelect
                        inputId="contacts-import-tags"
                        classNamePrefix="select"
                        isMulti
                        isClearable
                        cacheOptions
                        defaultOptions
                        loadOptions={loadTagOptions}
                        value={selectedTags}
                        onChange={(opts: readonly TagOption[] | null) =>
                          setSelectedTags([...(opts || [])])
                        }
                        placeholder={t("contacts.import.tagsPlaceholder")}
                        noOptionsMessage={() => t("contacts.filter.noTags")}
                        isDisabled={uploading}
                      />
                      <small className="text-muted d-block mt-1">
                        {t("contacts.import.tagsHelp")}
                      </small>
                    </div>

                    <div className="d-flex flex-wrap gap-2 justify-content-between">
                      <Button
                        color="light"
                        tag={Link}
                        to="/contacts"
                        disabled={uploading || previewing}
                      >
                        {t("contacts.import.back")}
                      </Button>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          type="button"
                          color="soft-primary"
                          disabled={
                            uploading || previewing || !file || !emailColumn
                          }
                          onClick={() => void runDryRun()}
                        >
                          {previewing ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              {t("contacts.import.preview.running")}
                            </>
                          ) : (
                            t("contacts.import.preview.button")
                          )}
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          disabled={
                            uploading || previewing || !file || !emailColumn
                          }
                        >
                          {uploading ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              {t("contacts.import.uploading")}
                            </>
                          ) : (
                            t("contacts.import.submit")
                          )}
                        </Button>
                      </div>
                    </div>

                    {previewing || previewProgress > 0 ? (
                      <div className="mt-3">
                        <p className="small text-muted mb-1">
                          {t("contacts.import.preview.progressHint")}
                        </p>
                        <Progress
                          value={Math.max(previewProgress, previewing ? 8 : 0)}
                          animated={previewing}
                          striped
                          color="primary"
                          style={{ height: "0.5rem" }}
                        />
                      </div>
                    ) : null}

                    {dryRun && (
                      <Alert color="info" className="mt-3 mb-0">
                        <p className="fw-medium mb-2">
                          {t("contacts.import.preview.title")}
                        </p>
                        <ul className="mb-2 ps-3">
                          <li>
                            {t("contacts.import.preview.wouldImport", {
                              count: dryRun.would_import_rows,
                            })}
                          </li>
                          {(dryRun.syntax_error_rows ?? 0) > 0 && (
                            <li>
                              {t("contacts.import.result.syntaxErrors", {
                                count: dryRun.syntax_error_rows,
                              })}
                            </li>
                          )}
                          {(dryRun.no_mx_rows ?? 0) > 0 && (
                            <li>
                              {t("contacts.import.result.noMx", {
                                count: dryRun.no_mx_rows,
                              })}
                            </li>
                          )}
                          {(dryRun.email_suppressed_rows ?? 0) > 0 && (
                            <li>
                              {t("contacts.import.result.emailSuppressed", {
                                count: dryRun.email_suppressed_rows,
                              })}
                            </li>
                          )}
                          {(dryRun.domain_suppressed_rows ?? 0) > 0 && (
                            <li>
                              {t("contacts.import.result.domainSuppressed", {
                                count: dryRun.domain_suppressed_rows,
                              })}
                            </li>
                          )}
                          {(dryRun.blocklisted_rows ?? 0) > 0 && (
                            <li>
                              {t("contacts.import.result.blocklisted", {
                                count: dryRun.blocklisted_rows,
                              })}
                            </li>
                          )}
                        </ul>
                        {dryRun.truncated ? (
                          <p className="small mb-2 text-muted">
                            {t("contacts.import.preview.truncated", {
                              scanned: dryRun.scanned_rows,
                              total: dryRun.total_rows,
                            })}
                          </p>
                        ) : null}
                        {(dryRun.rejected_beyond_sample ?? 0) > 0 ? (
                          <p className="small mb-2 text-muted">
                            {t("contacts.import.preview.beyondSample", {
                              count: dryRun.rejected_beyond_sample,
                            })}
                          </p>
                        ) : null}
                        {(dryRun.sample_rejects?.length ?? 0) > 0 ? (
                          <>
                            <p className="small fw-medium mb-1">
                              {t("contacts.import.preview.sampleTitle")}
                            </p>
                            <ul className="small mb-2 ps-3">
                              {dryRun.sample_rejects.slice(0, 5).map((r) => (
                                <li key={`${r.row}-${r.email}`}>
                                  {t("contacts.import.preview.sampleRow", {
                                    row: r.row,
                                    email: r.email || "—",
                                    reason:
                                      r.reason_label ||
                                      t(
                                        `contacts.import.rejectReason.${r.reason}`,
                                        { defaultValue: r.reason }
                                      ),
                                  })}
                                </li>
                              ))}
                            </ul>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                color="soft-secondary"
                                size="sm"
                                type="button"
                                onClick={() => {
                                  const lines = [
                                    "row,email,reason,reason_label",
                                    ...dryRun.sample_rejects.map((r) => {
                                      const label =
                                        r.reason_label ||
                                        t(
                                          `contacts.import.rejectReason.${r.reason}`,
                                          { defaultValue: r.reason }
                                        );
                                      const esc = (v: string) =>
                                        `"${String(v).replace(/"/g, '""')}"`;
                                      return [
                                        r.row,
                                        esc(r.email || ""),
                                        esc(r.reason),
                                        esc(label),
                                      ].join(",");
                                    }),
                                  ];
                                  const blob = new Blob(
                                    ["\uFEFF" + lines.join("\n")],
                                    { type: "text/csv;charset=utf-8" }
                                  );
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = "simulacao_rejeitados.csv";
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  URL.revokeObjectURL(url);
                                }}
                              >
                                {t("contacts.import.preview.downloadSample")}
                              </Button>
                              <Button
                                color="soft-primary"
                                size="sm"
                                type="button"
                                disabled={downloadingPreviewRejects}
                                onClick={() => void downloadFullPreviewRejects()}
                              >
                                {downloadingPreviewRejects ? (
                                  <Spinner size="sm" className="me-1" />
                                ) : null}
                                {t("contacts.import.preview.downloadFull")}
                              </Button>
                            </div>
                          </>
                        ) : (dryRun.syntax_error_rows ?? 0) +
                            (dryRun.no_mx_rows ?? 0) +
                            (dryRun.email_suppressed_rows ?? 0) +
                            (dryRun.domain_suppressed_rows ?? 0) +
                            (dryRun.blocklisted_rows ?? 0) >
                          0 ? (
                          <Button
                            color="soft-primary"
                            size="sm"
                            type="button"
                            disabled={downloadingPreviewRejects}
                            onClick={() => void downloadFullPreviewRejects()}
                          >
                            {downloadingPreviewRejects ? (
                              <Spinner size="sm" className="me-1" />
                            ) : null}
                            {t("contacts.import.preview.downloadFull")}
                          </Button>
                        ) : null}
                      </Alert>
                    )}
                  </Form>
                ) : (
                  <div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-medium">
                          {t(`contacts.import.status.${status || "pending"}`, {
                            defaultValue: t("contacts.import.status.pending"),
                          })}
                        </span>
                        {isPolling && <Spinner size="sm" color="primary" />}
                      </div>
                      <Progress
                        value={percent}
                        className="progress-lg"
                        animated={isPolling}
                        color={
                          status === "failed"
                            ? "danger"
                            : status === "completed"
                              ? "success"
                              : "primary"
                        }
                      >
                        {percent > 0 ? `${percent}%` : null}
                      </Progress>
                      <p className="text-muted small mt-2 mb-0">
                        {t("contacts.import.progressDetail", {
                          processed: progress?.processed_rows ?? 0,
                          total: progress?.total_rows ?? 0,
                        })}
                      </p>
                    </div>

                    {showStuckHint && status === "pending" && (
                      <Alert
                        color={
                          queueHealth?.worker_ok === false ? "danger" : "warning"
                        }
                      >
                        {queueHealth?.worker_ok === false
                          ? t("contacts.import.stuckHintWorkerDown")
                          : queueHealth?.worker_ok === true
                            ? t("contacts.import.stuckHintWorkerOk")
                            : t("contacts.import.stuckHint")}
                      </Alert>
                    )}

                    {status === "completed" && progress && (
                      <>
                        {(progress.imported_rows ?? 0) === 0 &&
                        (progress.total_rows ?? 0) > 0 ? (
                          <Alert color="warning">
                            <p className="mb-2 fw-medium">
                              {t("contacts.import.result.allRejectedTitle")}
                            </p>
                            <p className="mb-2">
                              {t("contacts.import.result.allRejectedBody")}
                            </p>
                            <ul className="mb-0 ps-3">
                              {(progress.syntax_error_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.syntaxErrors", {
                                    count: progress.syntax_error_rows,
                                  })}
                                </li>
                              )}
                              {(progress.no_mx_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.noMx", {
                                    count: progress.no_mx_rows,
                                  })}
                                </li>
                              )}
                              <ImportSuppressionItems progress={progress} />
                              {(progress.failed_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.failed", {
                                    count: progress.failed_rows,
                                  })}
                                </li>
                              )}
                              {(progress.blocklisted_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.blocklisted", {
                                    count: progress.blocklisted_rows,
                                  })}{" "}
                                  <Link
                                    to="/contacts/blocklist"
                                    className="fw-medium"
                                  >
                                    {t(
                                      "contacts.import.result.blocklistedLink"
                                    )}
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </Alert>
                        ) : (
                          <Alert color="success">
                            <p className="mb-2 fw-medium">
                              {t("contacts.import.result.title")}
                            </p>
                            <ul className="mb-0 ps-3">
                              <li>
                                {t("contacts.import.result.imported", {
                                  count: progress.imported_rows,
                                })}
                              </li>
                              <li>
                                {t("contacts.import.result.duplicated", {
                                  count: progress.duplicated_rows,
                                })}
                              </li>
                              {(progress.syntax_error_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.syntaxErrors", {
                                    count: progress.syntax_error_rows,
                                  })}
                                </li>
                              )}
                              {(progress.no_mx_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.noMx", {
                                    count: progress.no_mx_rows,
                                  })}
                                </li>
                              )}
                              <ImportSuppressionItems progress={progress} />
                              {(progress.failed_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.failed", {
                                    count: progress.failed_rows,
                                  })}
                                </li>
                              )}
                              {(progress.blocklisted_rows ?? 0) > 0 && (
                                <li>
                                  {t("contacts.import.result.blocklisted", {
                                    count: progress.blocklisted_rows,
                                  })}{" "}
                                  <Link
                                    to="/contacts/blocklist"
                                    className="fw-medium"
                                  >
                                    {t(
                                      "contacts.import.result.blocklistedLink"
                                    )}
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </Alert>
                        )}
                        {(progress.hygiene_risk_percent ?? 0) > 0 ||
                        (progress.hygiene_band &&
                          progress.hygiene_band !== "good") ? (
                          <Alert
                            color={
                              progress.hygiene_band === "risk"
                                ? "danger"
                                : progress.hygiene_band === "attention"
                                  ? "warning"
                                  : "info"
                            }
                            className="mt-3"
                          >
                            <p className="mb-1 fw-medium">
                              {t("contacts.import.hygieneScore.title")}{" "}
                              <Badge
                                color={
                                  progress.hygiene_band === "risk"
                                    ? "danger"
                                    : progress.hygiene_band === "attention"
                                      ? "warning"
                                      : "success"
                                }
                                className="align-middle"
                              >
                                {t(
                                  `contacts.import.hygieneScore.band.${progress.hygiene_band || "good"}`
                                )}
                              </Badge>
                            </p>
                            <p className="mb-0 small">
                              {progress.hygiene_band === "good"
                                ? t("contacts.import.hygieneScore.good", {
                                    percent: progress.hygiene_risk_percent ?? 0,
                                  })
                                : t("contacts.import.hygieneScore.body", {
                                    percent: progress.hygiene_risk_percent ?? 0,
                                    count: progress.hygiene_risk_rows ?? 0,
                                    total: progress.total_rows ?? 0,
                                  })}
                            </p>
                          </Alert>
                        ) : null}
                        {progress.has_rejected_csv ? (
                          <div className="mt-3">
                            <Button
                              color="soft-secondary"
                              size="sm"
                              disabled={downloadingRejected}
                              onClick={async () => {
                                if (!progress.id) return;
                                setDownloadingRejected(true);
                                try {
                                  await downloadImportRejectedCsv(progress.id);
                                  toast.success(
                                    t("contacts.import.rejected.downloaded")
                                  );
                                } catch (err: any) {
                                  toast.error(
                                    err?.message ||
                                      t("contacts.import.rejected.error")
                                  );
                                } finally {
                                  setDownloadingRejected(false);
                                }
                              }}
                            >
                              {downloadingRejected ? (
                                <Spinner size="sm" className="me-1" />
                              ) : null}
                              {t("contacts.import.rejected.download")}
                            </Button>
                          </div>
                        ) : null}
                      </>
                    )}

                    {status === "failed" && (
                      <Alert color="danger">
                        {progress?.failure_message ||
                          t("contacts.import.toast.failed")}
                      </Alert>
                    )}

                    <div className="d-flex flex-wrap gap-2 justify-content-between mt-4">
                      {status === "failed" || status === "completed" ? (
                        <Button color="light" onClick={resetForRetry}>
                          {status === "failed"
                            ? t("contacts.import.retry")
                            : t("contacts.import.another")}
                        </Button>
                      ) : (
                        <span />
                      )}
                      <Button
                        color="primary"
                        onClick={() => navigate("/contacts")}
                        disabled={isPolling}
                      >
                        {t("contacts.import.backToList")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {showForm && recent.length > 0 && (
              <Card className="mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("contacts.import.history.title")}
                  </h5>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("contacts.import.history.when")}</th>
                          <th>{t("contacts.import.history.status")}</th>
                          <th>{t("contacts.import.history.rows")}</th>
                          <th>{t("contacts.import.history.rejected")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((item) => (
                          <tr key={item.id}>
                            <td className="small text-muted">
                              {formatWhen(item.created_at)}
                            </td>
                            <td>
                              <Badge
                                color={
                                  item.status === "completed"
                                    ? "success"
                                    : item.status === "failed"
                                      ? "danger"
                                      : item.status === "processing"
                                        ? "primary"
                                        : "secondary"
                                }
                              >
                                {t(`contacts.import.status.${item.status}`, {
                                  defaultValue: item.status,
                                })}
                              </Badge>
                            </td>
                            <td className="small">
                              {item.processed_rows}/{item.total_rows || "—"}
                            </td>
                            <td className="small">
                              {item.rejected_count ?? "—"}
                            </td>
                            <td className="text-end">
                              <Button
                                color="soft-primary"
                                size="sm"
                                type="button"
                                onClick={() => resumeRecent(item.id)}
                              >
                                {t("contacts.import.history.open")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ImportContacts;
